import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';

/**
 * AppModal — a single shared modal used across the app for any of:
 *   - Error / warning / success / info notifications (single dismiss button)
 *   - Confirmation prompts (two buttons: secondary + primary)
 *   - Generic two-button modals where both labels and behaviors differ
 *
 * It centralizes Meridian wiring (Modal + ModalFooter + Row + Button +
 * optional Alert) so callers don't have to reach for those primitives
 * directly. The component is intentionally controlled and stateless — its
 * `open` flag, copy, button labels, and callbacks are owned by the
 * parent. That keeps the component reusable for redux-driven flows (the
 * existing notification queue) and one-off in-page flows alike.
 */

export type AppModalVariant = 'error' | 'success' | 'warning' | 'info' | 'confirmation';

/**
 * Maps a variant to the Alert component's `type` prop. `confirmation`
 * deliberately suppresses the Alert entirely (returns `null`); confirmation
 * dialogs are body-text only, no semantic icon banner.
 */
const getAlertType = (
  variant: AppModalVariant,
):
  | 'error'
  | 'warning'
  | 'success'
  | 'neutral'
  | null => {
  switch (variant) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'info':
      return 'neutral';
    case 'confirmation':
      return null;
  }
};

export interface AppModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Modal title rendered in the Meridian Modal header. */
  title: string;
  /**
   * Body content. A string is rendered inside a `<Text>`; pass a node for
   * richer bodies (lists, links, …).
   */
  message: string | React.ReactNode;
  /**
   * Visual variant. Drives both the Alert banner color/icon and a few
   * default labels. Pick `'confirmation'` for two-button prompts that
   * shouldn't display an Alert banner.
   */
  variant?: AppModalVariant;
  /** Label for the primary (right-most) button. */
  primaryLabel?: string;
  /**
   * Label for the secondary (left) button. When omitted, only the
   * primary button is rendered (single-action modal).
   */
  secondaryLabel?: string;
  /**
   * Fires when the primary button is clicked. Required for any modal,
   * since there is always a primary action.
   */
  onPrimary: () => void;
  /**
   * Fires when the secondary button is clicked. Only used when
   * `secondaryLabel` is provided.
   */
  onSecondary?: () => void;
  /**
   * Fires when the user dismisses the modal via Meridian's close
   * affordance / backdrop / Escape. Defaults to `onSecondary` (when one
   * is provided) or `onPrimary` so dismissal still has a sensible fallback.
   */
  onClose?: () => void;
  /** Optional test id applied to the body container. */
  'data-testid'?: string;
}

const AppModal: React.FC<AppModalProps> = ({
  open,
  title,
  message,
  variant = 'info',
  primaryLabel = 'OK',
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
  'data-testid': testId,
}) => {
  const alertType = getAlertType(variant);

  // Pick a sensible close handler. If a caller doesn't pass `onClose` we
  // fall back to whichever non-destructive button they did wire up.
  const handleClose = onClose ?? onSecondary ?? onPrimary;

  return (
    <Modal
      title={title}
      open={open}
      onClose={handleClose}
      scrollContainer="viewport"
      aria-describedby="app-modal-description"
    >
      {alertType ? (
        <Alert type={alertType} size="large">
          {typeof message === 'string' ? (
            <Text id="app-modal-description">{message}</Text>
          ) : (
            <Box id="app-modal-description">{message}</Box>
          )}
        </Alert>
      ) : (
        <Box data-testid={testId}>
          {typeof message === 'string' ? (
            <Text id="app-modal-description">{message}</Text>
          ) : (
            <Box id="app-modal-description">{message}</Box>
          )}
        </Box>
      )}

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          {secondaryLabel && onSecondary && (
            <Button
              type="secondary"
              size="small"
              onClick={onSecondary}
              data-testid={
                testId ? `${testId}-secondary-button` : 'app-modal-secondary-button'
              }
            >
              {secondaryLabel}
            </Button>
          )}
          <Button
            type="primary"
            size="small"
            onClick={onPrimary}
            data-testid={
              testId ? `${testId}-primary-button` : 'app-modal-primary-button'
            }
          >
            {primaryLabel}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default AppModal;
